import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { StyledButton } from "../../../shared/components/buttons/custom-button";
import CustomTable from "../../../shared/components/table/custom-table";
import ModalPopup from "../../../shared/components/modals/custom-popup";
import FormBuilder from "../../../shared/components/form-builder/form-builder";
import { useApi } from "../../../shared/hooks/useApi";
import { useNavigate, useParams } from "react-router-dom";
import useNotification from "../../../shared/hooks/useNotification";
import * as Yup from 'yup';
import { ETCAppConfig } from "../../../shared/app-configs/etc-app-configs";
import { LoaderComponent } from "../../../shared/components/loaders/page-loader";
import { NavLink } from "react-router-dom";
import { ActionsMenu } from "../../../shared/components/table/custom-actions";

interface IShipmentConsigneeDetail {
  tcShipmentConsigneeOrder: string;
  tcShipmentNo: string;
  tcShipmentDate: string;
  tcShipmentDocNo: string;
  tcShipmentGrossShippingWeight: number;
  tcShipmentInvoiceReferences: string[];
  consigneeName: string;
  consigneeTeId: string;
  isConsigneeTeCertified: string;
  consigneeAddress1: string;
  consigneeAddress2: string;
  consigneeAddress3: string;
  consigneePostcode: string;
  consigneeTown: string;
  consigneeStateOrProvince: string;
  consigneeCountryOrArea: string;
}

interface IShipmentsApiResponse {
  etcId: string;
  tcGrossShippingWeight: number; // This is the total shipping weight
  tcShipmentConsigneeDetails: IShipmentConsigneeDetail[];
}

const Shipments: React.FC = () => {
  // We assume your route is something like /shipments/:etcId
  const { etcId, formtype } = useParams<string>();
  const { notify } = useNotification();
  const navigate = useNavigate();

  // We'll store the entire array of shipments from the API
  const [data, setData] = useState<IShipmentConsigneeDetail[]>([]);


  // We'll store the single record to show in the read-only form
  const [selectedShipment, setSelectedShipment] = useState<IShipmentConsigneeDetail | null>(null);

  // For controlling the modal open/close
  const [modalOpen, setModalOpen] = useState(false);

  // For FormBuilder ref (in case you need to access form methods)
  const formRef = useRef<any>(null);

  const { executeGet, loading } = useApi();

  // 1) Fetch data in useEffect with a single GET API
  useEffect(() => {
    if (etcId) {
      fetchShipmentsData(etcId);
    }
  }, [etcId]);

  const fetchShipmentsData = async (etcId: string) => {
    try {
      // Example: Replace "API_ENDPOINT" with your real endpoint
      // and pass etcId as a param
      const response: any = await executeGet(ETCAppConfig.GET_ETC_SHIPMENTS, { etcId });
      // Suppose the response looks like:
      // {
      //   etcId: "123",
      //   tcGrossShippingWeight: 1,
      //   tcShipmentConsigneeDetails: [ {...}, {...} ]
      // }
      setData(response.data?.tcShipmentConsigneeDetails || []); // For table
    } catch (error) {
      console.error("Error fetching shipments:", error);
      notify("Error fetching shipment data", "error");
    }
  };

  // 2) Table columns with a single "View" action
  const columns = useMemo<MRT_ColumnDef<IShipmentConsigneeDetail>[]>(() => [
    {
      accessorKey: "tcShipmentConsigneeOrder",
      header: "Consignee Order",
    },
    {
      accessorKey: "tcShipmentNo",
      header: "Shipment No.",
    },
    {
      accessorKey: "tcShipmentDate",
      header: "Shipment Date",
    },
    {
      accessorKey: "tcShipmentDocNo",
      header: "Shipment Doc No.",
    },
    {
      accessorKey: "tcShipmentGrossShippingWeight",
      header: "Gross Shipping Weight (kg)",
    },
    {
      accessorKey: "tcShipmentInvoiceReferences",
      header: "Invoice References",
      Cell: ({ cell }) => cell.getValue<string[]>()?.join(", "), // Display array as comma-separated string
    },
    {
      accessorKey: "consigneeName",
      header: "Consignee Name",
    },
    {
      accessorKey: "consigneeTeId",
      header: "Consignee TE ID",
    },
    {
      accessorKey: "isConsigneeTeCertified",
      header: "TE Certified",
      Cell: ({ cell }) => (cell.getValue() === "true" ? "Yes" : "No"), // Display 'Yes' or 'No' for certification
    },
    {
      accessorKey: "consigneeAddress1",
      header: "Address Line 1",
    },
    {
      accessorKey: "consigneeAddress2",
      header: "Address Line 2",
    },
    {
      accessorKey: "consigneeAddress3",
      header: "Address Line 3",
    },
    {
      accessorKey: "consigneePostcode",
      header: "Postcode",
    },
    {
      accessorKey: "consigneeTown",
      header: "Town",
    },
    {
      accessorKey: "consigneeStateOrProvince",
      header: "State/Province",
    },
    {
      accessorKey: "consigneeCountryOrArea",
      header: "Country/Area",
    },
  ], []);
  

  // On "View" click, open modal with read-only form
  const handleView = (row: any) => {
    const rowData = row.original as IShipmentConsigneeDetail;
    setSelectedShipment(rowData);
    setModalOpen(true);
  };

  // 3) Read-only form config
  //    All fields are "text" and "disabled: true"  
  const formConfig = {
    pageName: "shipments-readonly",
    initialValues: selectedShipment || {},
    validationSchema: Yup.object().shape({}),  // Proper Yup schema
    sections: [
      {
        title: "Shipment Details", 
        fields: [
          {
            label: "Shipment No.",
            name: "tcShipmentNo",
            type: "text",
            required: false,
            disabled: true,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Shipment No." },
          },
          {
            label: "Shipment Date",
            name: "tcShipmentDate",
            type: "text",
            required: false,
            disabled: true,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Shipment Date" },
          },
          {
            label: "Shipment Doc No.",
            name: "tcShipmentDocNo",
            type: "text",
            required: false,
            disabled: true,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Shipment Doc No." },
          },
          {
            label: "Gross Shipping Weight (kg)",
            name: "tcShipmentGrossShippingWeight",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Gross Shipping Weight" },
          },
          {
            label: "Invoice References",
            name: "tcShipmentInvoiceReferences",
            type: "text",
            disabled: true,
            required: false,
            // If invoice references is an array, you might want to convert it to a string
            // So "value" can be a joined string
            // But for simplicity, let form-builder handle it if it can
            gridSize: { xs: 12, md: 12 },
            props: { placeholder: "Invoice References (comma separated)" },
          },
        ],
      },
      {
        title: "Consignee Details",
        fields: [
          {
            label: "Consignee Name",
            name: "consigneeName",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee Name" },
          },
          {
            label: "Consignee TE ID",
            name: "consigneeTeId",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee TE ID" },
          },
          {
            label: "Consignee Address 1",
            name: "consigneeAddress1",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee Address 1" },
          },
          {
            label: "Consignee Address 2",
            name: "consigneeAddress2",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee Address 2" },
          },
          {
            label: "Consignee Address 3",
            name: "consigneeAddress3",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee Address 3" },
          },
          {
            label: "Consignee Postcode",
            name: "consigneePostcode",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee Postcode" },
          },
          {
            label: "Consignee Town",
            name: "consigneeTown",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee Town" },
          },
          {
            label: "Consignee State/Province",
            name: "consigneeStateOrProvince",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee State/Province" },
          },
          {
            label: "Consignee Country/Area",
            name: "consigneeCountryOrArea",
            type: "text",
            disabled: true,
            required: false,
            gridSize: { xs: 12, md: 6 },
            props: { placeholder: "Consignee Country/Area" },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      // It's read-only, so you might not do anything here
    },
  };

  const totalGrossWeight = data.length
  ? data.reduce((sum, item) => {
      const weight = typeof item.tcShipmentGrossShippingWeight === "string"
        ? parseFloat(item.tcShipmentGrossShippingWeight)
        : item.tcShipmentGrossShippingWeight;
      return sum + (isNaN(weight) ? 0 : weight);
    }, 0)
  : 0;

  const handleSubmit = () => {
    navigate(`/etc/${formtype}/certified-raw-materials-std-details/${etcId}`)
  };

  return (
    <>
    {loading ? <LoaderComponent /> :
    <>
    <Box sx={{ mt: 4, mb: 4 }}>
    <Typography variant="h6">Total Gross Shipping Weight: {totalGrossWeight}</Typography>
    </Box>
      
      {/* <Typography>Gross Shipping Weight: {totalGrossWeight}</Typography> */}
      {/* Table of shipments */}
      <Box sx={{ mt: 14, mb: 4 }} display="grid">
      <CustomTable
        columns={columns}
        data={data}
        enableTopToolbar={false}
        enableBottomToolbar={true}
        enableRowActions={false}
        renderRowActions={({ row }) => (
          <ActionsMenu
            onView={() => handleView(row)}
            row={row}
          />
        )}
      />
      </Box>
      {/* Modal with read-only form */}
      <ModalPopup open={modalOpen} onClose={() => setModalOpen(false)} title="View Shipment">
        <FormBuilder formConfig={formConfig} formRef={formRef} />
      </ModalPopup>
      <Box sx={{
        display: "flex", justifyContent: "flex-end", mt: 2
      }}>
        <StyledButton variant="contained" onClick={handleSubmit}>
          Next
        </StyledButton>
        {/* <NavLink to="/tc-dashboard">
          <StyledButton variant="contained">
            Cancel
          </StyledButton>
        </NavLink> */}
      </Box>
      </>
    }
    </>
  );
};

export default Shipments;